/**
 * Real-time countdown derived from an absolute end time.
 * Uses dual scheduling: requestAnimationFrame for visible tabs, setInterval for hidden tabs.
 * Always computes remaining time from absolute deadline to prevent drift and handle sleep/wake.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Real-time countdown hook that derives seconds from an absolute deadline
 * @param {Object} config - Configuration object
 * @param {number} config.endAtMs - Epoch ms when countdown should hit 0
 * @param {Function} config.onEnd - Called once when remaining <= 0
 * @param {number} config.visibleUpdateMs - Soft minimum update cadence while visible (via rAF loop)
 * @param {number} config.hiddenUpdateMs - Interval cadence when tab is hidden
 * @returns {number} Current remaining seconds
 */
export default function useRealTimeCountdown({ 
  endAtMs, 
  onEnd, 
  visibleUpdateMs = 250, 
  hiddenUpdateMs = 1000 
}) {
  const [seconds, setSeconds] = useState(() => initialSeconds(endAtMs));
  const rafRef = useRef(null);
  const intervalRef = useRef(null);
  const endedRef = useRef(false);
  // The latest onEnd, read when the countdown ends. Callers pass a callback that is rebuilt as
  // their state changes (every keystroke, for the quiz player); depending on it directly would
  // tear down and restart the timers each time, and re-arm the "ended" flag.
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  // Compute remaining seconds from absolute deadline and update state
  const computeAndSet = useCallback(() => {
    // If endAtMs is null/undefined, don't compute anything
    if (!endAtMs) {
      return;
    }
    
    const remainingMs = Math.max(0, endAtMs - Date.now());
    const next = Math.ceil(remainingMs / 1000);
    
    // Only update state if the value actually changed
    setSeconds(prev => (prev !== next ? next : prev));
    
    // Call onEnd exactly once when time reaches 0
    if (!endedRef.current && remainingMs <= 0) {
      endedRef.current = true;
      onEndRef.current?.();
    }
  }, [endAtMs]);

  // Start smooth animation loop for visible tabs using requestAnimationFrame
  const startVisibleLoop = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    let last = 0;
    
    const step = (ts) => {
      if (!document.hidden) {
        // Throttle to ~visibleUpdateMs using timestamp deltas
        if (!last || ts - last >= visibleUpdateMs) {
          computeAndSet();
          last = ts;
        }
        rafRef.current = requestAnimationFrame(step);
      }
    };
    
    rafRef.current = requestAnimationFrame(step);
  }, [computeAndSet, visibleUpdateMs]);

  // Start interval loop for hidden tabs
  const startHiddenLoop = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(computeAndSet, hiddenUpdateMs);
  }, [computeAndSet, hiddenUpdateMs]);

  // Stop all scheduling loops
  const stopAll = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    clearInterval(intervalRef.current);
    rafRef.current = null;
    intervalRef.current = null;
  }, []);

  // Handle visibility changes and start appropriate scheduling loop
  const handleVisibilityChange = useCallback(() => {
    stopAll();
    if (document.hidden) {
      startHiddenLoop();
    } else {
      startVisibleLoop();
    }
  }, [stopAll, startHiddenLoop, startVisibleLoop]);

  // Initialize countdown and set up visibility handling
  useEffect(() => {
    // A new deadline (or none) starts a fresh run: the end callback may fire again for it.
    endedRef.current = false;
    // Only start if we have a valid endAtMs
    if (endAtMs) {
      // Initialize immediately so UI reflects the current remaining time
      computeAndSet();
      
      // Start appropriate loop based on current visibility
      handleVisibilityChange();
    } else {
      // Stop all loops if no valid endAtMs
      stopAll();
    }
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopAll();
    };
  }, [endAtMs, computeAndSet, handleVisibilityChange, stopAll]);

  // Browsers throttle timers in background tabs (to about once a minute after a few minutes hidden),
  // which could delay the end of a timed quiz past the server's grace period. A worker's timer is
  // not throttled that way, so it wakes the countdown at the deadline. Skipped silently where
  // workers or blob URLs are unavailable (tests, strict CSP): the interval/visibility paths remain.
  useEffect(() => {
    if (!endAtMs || typeof Worker === 'undefined' || typeof Blob === 'undefined' || !window.URL?.createObjectURL) {
      return undefined;
    }
    let worker = null;
    let url = null;
    try {
      const source = 'self.onmessage=function(e){setTimeout(function(){self.postMessage(1)},e.data)}';
      url = window.URL.createObjectURL(new Blob([source], { type: 'application/javascript' }));
      worker = new Worker(url);
      worker.onmessage = () => computeAndSet();
      worker.postMessage(Math.max(0, endAtMs - Date.now()) + 50);
    } catch (e) {
      // no worker timer; the other paths still run
    }
    return () => {
      if (worker) worker.terminate();
      if (url) window.URL.revokeObjectURL(url);
    };
  }, [endAtMs, computeAndSet]);

  return seconds;
}

/**
 * Calculate initial seconds from endAtMs
 * @param {number} endAtMs - Epoch ms when countdown should hit 0
 * @returns {number} Initial remaining seconds
 */
function initialSeconds(endAtMs) {
  if (!endAtMs) {
    return 0;
  }
  const ms = Math.max(0, endAtMs - Date.now());
  return Math.ceil(ms / 1000);
}
